import { format, parseISO, isValid } from 'date-fns';
import { mapInsightsKpiToCardProps } from '../../../utils/mapInsightsKpiCard';
import { getPromptPulseMetricDefinition } from './promptPulseMetricDefinitions';

const KPI_TITLES = {
  our_company_mentions: 'OUR COMPANY MENTIONS',
  our_average_position: 'OUR AVERAGE POSITION',
  mention_share: 'MENTION SHARE',
  share_of_voice: 'SHARE OF VOICE',
  active_prompts: 'ACTIVE PROMPTS',
};

const COMPANY_SUFFIX_RE = /\s+(Life\s+Insurance\s+Company|Insurance\s+Company)\s*$/i;

/**
 * SVG axis font size in viewBox user units.
 * CSS px on SVG <text> does not scale with viewBox — use this instead.
 * @param {number} viewBoxHeight — chart viewBox height
 * @param {number} targetPx — desired screen px at 1:1 viewBox render
 * @param {number} refHeight — reference chart height (Mention Share = 400)
 */
export function geoSvgAxisFontSize(viewBoxHeight, targetPx = 12, refHeight = 400) {
  return Number((targetPx * (viewBoxHeight / refHeight)).toFixed(2));
}

/** X-axis label for Mention Share Over Time (e.g. 01-May). */
export function formatGeoChartDayLabel(isoDate) {
  const parsed = parseISO(String(isoDate || ''));
  if (!isValid(parsed)) return String(isoDate || '');
  return format(parsed, 'dd-MMM');
}

/** Tooltip heading for a chart day (e.g. May 1, 2026). */
export function formatGeoChartDayTooltipTitle(isoDate) {
  const parsed = parseISO(String(isoDate || ''));
  if (!isValid(parsed)) return String(isoDate || '');
  return format(parsed, 'MMMM d, yyyy');
}

/** X-axis ticks spaced backward from the most recent day (avoids end-label overlap). */
export function shouldShowGeoChartDayTick(index, total) {
  if (total <= 1) return true;
  if (total <= 10) return true;

  const lastIndex = total - 1;
  const offsetFromEnd = lastIndex - index;

  if (total <= 14) {
    return offsetFromEnd % 2 === 0;
  }

  if (total <= 35) {
    return offsetFromEnd % 7 === 0;
  }

  const step = Math.max(7, Math.ceil(total / 8));
  return offsetFromEnd % step === 0;
}

export function formatCompanyName(name, maxLength = 28) {
  const cleaned = String(name || '').replace(COMPANY_SUFFIX_RE, '').trim();
  if (!cleaned) return '';
  if (cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, maxLength - 1)}...`;
}

export function formatGeoCoveragePct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${n % 1 === 0 ? Math.round(n) : n.toFixed(1)}%`;
}

export function formatGeoAvgPosition(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

/** Citation share for the Citations table (e.g. 12.5%). */
export function formatGeoCitationShare(share) {
  const n = Number(share);
  if (!Number.isFinite(n) || n <= 0) return '0%';
  return n % 1 === 0 ? `${Math.round(n)}%` : `${n.toFixed(1)}%`;
}

/** Compact URL for our-citations table (hostname + path, no protocol). */
export function formatGeoCitationUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, '');
    const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
    return `${host}${path}`;
  } catch {
    return raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  }
}

export function formatQuadrantLabel(quadrantKey) {
  if (quadrantKey === 'niche') return 'Niche';
  if (quadrantKey === 'leaders') return 'Leaders';
  if (quadrantKey === 'lowConversion') return 'Low Conversion';
  return 'Low Performance';
}

/** Display label for sentiment pills (e.g. neutral → Neutral, N/A unchanged). */
export function formatSentimentLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw.toLowerCase() === 'n/a') return 'N/A';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** KPICard props for GEO Insights — subtitles/compare notes kept, % delta chip omitted. */
const GEO_PERCENT_KPI_KEYS = new Set(['mention_share', 'share_of_voice']);

export function mapGeoKpiToCardProps(kpi) {
  const { compareChip, ...rest } = mapInsightsKpiToCardProps(kpi);
  if (kpi?.titleTooltip) {
    rest.titleTooltip = kpi.titleTooltip;
  }
  const key = String(kpi?.key || '');
  if (GEO_PERCENT_KPI_KEYS.has(key)) {
    if (rest.value != null && rest.value !== '—') {
      rest.value = formatGeoCoveragePct(rest.value);
    }
    if (rest.compareNote?.benchmark != null) {
      rest.compareNote = {
        ...rest.compareNote,
        benchmark: formatGeoCoveragePct(rest.compareNote.benchmark),
      };
    }
  }
  return rest;
}

export function mapGeoKpiCards(rawCards = []) {
  return rawCards.map((card) => {
    const key = String(card?.key || '');
    if (key === 'active_prompts') {
      return {
        key,
        title: KPI_TITLES[key],
        titleTooltip: getPromptPulseMetricDefinition(key),
        value: String(card?.value ?? 0),
        subtitle: `${Number(card?.total) || 0} total in query bank`,
      };
    }

    return {
      key,
      title: KPI_TITLES[key] || key,
      titleTooltip: getPromptPulseMetricDefinition(key),
      our: card?.our ?? 0,
      competitor: card?.competitor && typeof card.competitor === 'object' ? card.competitor : {},
      higherIsBetter: card?.higherIsBetter,
    };
  });
}

/** Numeric sort for GEO prompt ids (TEXT ids like "3" vs "29"). */
export function compareGeoQueryIds(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a) !== '' && String(b) !== '') {
    return na - nb;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}
